import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';

import CSS from './styles';
import { TODAY, TODAY_DISP, df, SEED } from './utils/dates';
import { buildSignals, buildInsights, buildDailyBrief, buildSystemPrompt, buildPatternInsights, buildProactiveAlerts, buildPatternMemories } from './utils/signals';
import { detectMemories } from './utils/memory';
import { supabase, getCachedSession } from './utils/supabase';
import { loadState, saveState, getOfflineQueue, addToOfflineQueue } from './utils/state';

import LoginScreen   from './components/LoginScreen';
import { OnboardingFlow } from './components/shared';
import DashboardPage from './components/DashboardPage';
import ChatPage      from './components/ChatPage';
import VoiceModal    from './components/VoiceModal';
import CalendarPage  from './components/CalendarPage';
import TasksPage     from './components/TasksPage';
import GoalsPage     from './components/GoalsPage';
import VinylPage     from './components/VinylPage';
import FinancePage   from './components/FinancePage';
import HealthPage    from './components/HealthPage';
import SettingsPage  from './components/SettingsPage';
import WeeklyPage    from './components/WeeklyPage';
import InboxPage     from './components/InboxPage';
import TimelinePage  from './components/TimelinePage';
import ContactsPage  from './components/ContactsPage';
import RemindersPage from './components/RemindersPage';
import MorningBrief  from './components/MorningBrief';
import CommandBar    from './components/CommandBar';
import ShiftMode     from './components/ShiftMode';

// ── Navigation ────────────────────────────────────────────────────────────────

const NAV_CORE = [
  { id: 'dashboard', icon: '⊞', label: 'Dashboard' },
  { id: 'tasks',     icon: '☰', label: 'Tasks' },
  { id: 'calendar',  icon: '▣', label: 'Calendar' },
  { id: 'finance',   icon: '◇', label: 'Finance' },
  { id: 'health',    icon: '♡', label: 'Health' },
  { id: 'goals',     icon: '◎', label: 'Goals' },
  { id: 'vinyl',     icon: '◉', label: 'Vinyl' },
];

const NAV_ADV = [
  { id: 'weekly',    icon: '↻', label: 'Review' },
  { id: 'timeline',  icon: '⁞', label: 'Timeline' },
  { id: 'settings',  icon: '⊙', label: 'Settings' },
];

const BOT_NAV = [
  { id: 'dashboard', icon: '⊞', label: 'Home' },
  { id: 'tasks',     icon: '☰', label: 'Tasks' },
  { id: 'ask',       icon: 'M.', label: 'Ask Mylo' },
  { id: 'health',    icon: 'heart', label: 'Health' },
  { id: 'calendar',  icon: 'cal', label: 'Cal' },
];

// ── Error Boundary ────────────────────────────────────────────────────────────

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, retries: 0 };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('[Mylo] Crash:', error.message, info.componentStack);
    window._myloError = { message: error.message, stack: error.stack };
  }
  handleRetry() {
    if (this.state.retries < 3) {
      this.setState({ hasError: false, error: null, retries: this.state.retries + 1 });
    } else {
      window.location.reload();
    }
  }
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{ position:'fixed', inset:0, background:'#080808', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:16, padding:24, fontFamily:'-apple-system,sans-serif' }}>
        <img src="/icon-192.png" alt="Mylo" style={{ width:56, height:56, borderRadius:12, opacity:.6 }} />
        <div style={{ fontSize:17, fontWeight:600, color:'#eeebe5', letterSpacing:'-.3px' }}>Something went wrong</div>
        <div style={{ fontSize:13, color:'#484643', textAlign:'center', maxWidth:280, lineHeight:1.6 }}>
          Mylo hit an unexpected error. Your data is safe in localStorage.
        </div>
        <button onClick={() => this.handleRetry()}
          style={{ marginTop:8, height:40, padding:'0 24px', background:'#b8944a', border:'none', borderRadius:8, color:'#080808', fontSize:14, fontWeight:600, cursor:'pointer' }}>
          Reload app
        </button>
        {this.state.error && (
          <details style={{ marginTop:8, fontSize:11, color:'#484643', fontFamily:'monospace', maxWidth:400, wordBreak:'break-all' }}>
            <summary style={{ cursor:'pointer', color:'#2a2927' }}>Error details</summary>
            {String(this.state.error)}
          </details>
        )}
      </div>
    );
  }
}

// ── Splash hider (removes HTML loading screen once React mounts) ──────────────

function HideSplash() {
  useEffect(() => {
    if (typeof window.__hideSplash === 'function') {
      window.__hideSplash();
      window.__hideSplash = null;
    }
  }, []);
  return null;
}

// ── Initial tab from URL params (PWA shortcuts) ───────────────────────────────

function getInitialTab() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tab'))                          return params.get('tab');
    if (params.get('action') === 'log_workout')     return 'health';
    if (params.get('action') === 'add_task')        return 'tasks';
  } catch {}
  return 'ask';
}

// ── Main App ──────────────────────────────────────────────────────────────────

export default function App() {

  // ── Core state ──────────────────────────────────────────────────────────────
  const [tab,          setTab]          = useState(getInitialTab);
  const [st,           setSt]           = useState(() => loadState());
  const [toast,        setToast]        = useState(null);
  const [calView,      setCalView]      = useState('week');
  const [voiceOpen,    setVoiceOpen]    = useState(false);
  const [qcOpen,       setQcOpen]       = useState(false);
  const [qcVal,        setQcVal]        = useState('');
  const [qcRes,        setQcRes]        = useState(null);
  const [showAddV,     setShowAddV]     = useState(false);
  const [showAddF,     setShowAddF]     = useState(false);
  const [mobNavOpen,   setMobNavOpen]   = useState(false);

  // ── Auth state ───────────────────────────────────────────────────────────────
  const [user,         setUser]         = useState(() => getCachedSession());
  const [authReady,    setAuthReady]    = useState(() => !supabase || !!getCachedSession());
  const [syncing,      setSyncing]      = useState(false);
  const [queuedOps,    setQueuedOps]    = useState(() => getOfflineQueue().length);
  const [cloudSynced,  setCloudSynced]  = useState(false);
  const [briefDismissed, setBriefDismissed] = useState(() => {
    try { return localStorage.getItem('mylo_brief_dismissed') === new Date().toISOString().slice(0, 10); }
    catch { return false; }
  });

  // ── Memory + connectivity state ──────────────────────────────────────────────
  const [memQueue,     setMemQueue]     = useState([]);
  const [memShowing,   setMemShowing]   = useState(null);
  const [isOnline,     setIsOnline]     = useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  // ── Refs ─────────────────────────────────────────────────────────────────────
  const syncTimerRef   = useRef(null);
  const lastSyncedRef  = useRef(null);
  const versionRef     = useRef(0);
  const syncBusyRef    = useRef(false);
  const seenMemKeysRef = useRef(new Set());

  // ── Cloud save (debounced) ───────────────────────────────────────────────────
  // Merge local changes over server state on version conflict.
  const mergeOnConflict = (local, server) => {
    const merged = { ...(server || {}) };
    let lastSynced = null;
    try { lastSynced = lastSyncedRef.current ? JSON.parse(lastSyncedRef.current) : null; } catch {}
    const keys = new Set([
      ...Object.keys(local || {}),
      ...Object.keys(lastSynced || {}),
    ]);
    for (const k of keys) {
      const localVal  = JSON.stringify(local ? local[k] : undefined);
      const syncedVal = JSON.stringify(lastSynced ? lastSynced[k] : undefined);
      if (localVal !== syncedVal) merged[k] = local[k];
    }
    return merged;
  };

  const saveToCloud = useCallback(async (state, _retried = false) => {
    if (!supabase) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    if (syncBusyRef.current && !_retried) return; // save in flight; debounce will re-fire
    const session = (await supabase.auth.getSession()).data.session;
    if (!session) return;
    const stateStr = JSON.stringify(state);
    if (stateStr === lastSyncedRef.current) return;
    try {
      syncBusyRef.current = true;
      setSyncing(true);
      const res = await fetch('/api/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'If-Match': String(versionRef.current),
        },
        body: JSON.stringify({ data: state }),
      });
      if (res.ok) {
        const out = await res.json().catch(() => ({}));
        if (typeof out.version === 'number') versionRef.current = out.version;
        lastSyncedRef.current = stateStr;
      } else if (res.status === 409 && !_retried) {
        // Another device wrote first. Merge our changes over theirs, retry once.
        const out = await res.json().catch(() => ({}));
        if (typeof out.version === 'number') versionRef.current = out.version;
        const merged = mergeOnConflict(state, out.data || null);
        setSt(merged);
        saveState(merged);
        await saveToCloud(merged, true);
        return;
      } else {
        try {
          const reg = await navigator.serviceWorker?.ready;
          if (reg?.sync) await reg.sync.register('mylo-sync');
        } catch {}
      }
    } catch (e) {
      console.warn('Cloud save failed:', e);
      try {
        const reg = await navigator.serviceWorker?.ready;
        if (reg?.sync) await reg.sync.register('mylo-sync');
      } catch {}
    } finally {
      syncBusyRef.current = false;
      setSyncing(false);
    }
  }, []); // eslint-disable-line

  // ── Cloud state loader ───────────────────────────────────────────────────────
  async function loadCloudState(token) {
    try {
      const res = await fetch('/api/sync', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) { setCloudSynced(true); return; }
      const payload = await res.json();
      const state = payload.data !== undefined ? payload.data : payload.state;
      if (typeof payload.version === 'number') versionRef.current = payload.version;
      if (state) {
        const localKey = st.anthropicKey ||
          (() => { try { return localStorage.getItem('mylo_api_key') || ''; } catch { return ''; } })();
        const localDiscogs =
          (() => { try { return localStorage.getItem('mylo_discogs_token') || ''; } catch { return ''; } })();
        const merged = {
          ...SEED, ...state,
          anthropicKey: state.anthropicKey || localKey || '',
          discogsToken: state.discogsToken || localDiscogs || '',
          prefs:   { ...SEED.prefs,   ...(state.prefs   || {}) },
          profile: { ...SEED.profile, ...(state.profile || {}) },
        };
        setSt(merged);
        saveState(merged);
        lastSyncedRef.current = JSON.stringify(merged);
        if (merged.anthropicKey) {
          try { localStorage.setItem('mylo_api_key', merged.anthropicKey); } catch {}
        }
        if (merged.discogsToken) {
          try { localStorage.setItem('mylo_discogs_token', merged.discogsToken); } catch {}
        }
      }
      setCloudSynced(true);
    } catch (e) {
      console.warn('Cloud load failed:', e);
      setCloudSynced(true);
    }
  }

  // ── Auth initialisation ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!supabase) return;
    const hasCached = !!getCachedSession();
    let authTimeout = null;

    // Safety net: unblock loading screen after 2s if no cached session
    if (!hasCached) {
      authTimeout = setTimeout(() => {
        setAuthReady(true);
        setCloudSynced(true);
      }, 2000);
    }

    // Hard fallback: always unblock cloudSynced after 8s
    const cloudTimeout = setTimeout(() => setCloudSynced(true), 8000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (authTimeout) { clearTimeout(authTimeout); authTimeout = null; }
      setUser(session?.user ?? null);
      setAuthReady(true);
      if (event === 'SIGNED_IN' && session?.user) {
        loadCloudState(session.access_token).catch(() => {});
      }
      if (event === 'SIGNED_OUT') {
        setSt(SEED);
        saveState(SEED);
      }
    });

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (authTimeout) { clearTimeout(authTimeout); authTimeout = null; }
      setUser(session?.user ?? null);
      setAuthReady(true);
      if (session?.user) {
        loadCloudState(session.access_token).catch(() => { setCloudSynced(true); });
      } else {
        setCloudSynced(true);
      }
    }).catch(() => {
      if (authTimeout) { clearTimeout(authTimeout); authTimeout = null; }
      setAuthReady(true);
    });

    return () => {
      subscription.unsubscribe();
      if (authTimeout) clearTimeout(authTimeout);
      clearTimeout(cloudTimeout);
    };
  }, []); // eslint-disable-line

  // ── Theme ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const theme = st.theme || 'dark';
    if (theme === 'system') {
      document.documentElement.removeAttribute('data-theme');
    } else {
      document.documentElement.setAttribute('data-theme', theme);
    }
  }, [st.theme]);

  // ── Persist to localStorage + debounced cloud save ───────────────────────────
  useEffect(() => {
    saveState(st);
    if (st.anthropicKey) {
      try { localStorage.setItem('mylo_api_key', st.anthropicKey); } catch {}
    }
    if (st.discogsToken) {
      try { localStorage.setItem('mylo_discogs_token', st.discogsToken); } catch {}
    }
    if (st.profile?.discogsUser) {
      try { localStorage.setItem('mylo_discogs_user', st.profile.discogsUser); } catch {}
    }
    if (supabase && user) {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
      syncTimerRef.current = setTimeout(() => saveToCloud(st), 2000);
    }
  }, [st, user, saveToCloud]); // eslint-disable-line

  // ── Offline queue tracking ───────────────────────────────────────────────────
  useEffect(() => {
    if (!isOnline) {
      addToOfflineQueue({ type: 'state_change', ts: Date.now() });
      setQueuedOps(getOfflineQueue().length);
    }
  }, [st, isOnline]); // eslint-disable-line

  // ── Online / offline detection ────────────────────────────────────────────────
  useEffect(() => {
    const goOnline = () => {
      setIsOnline(true);
      if (supabase && user) {
        const t = setTimeout(() => saveToCloud(st), 1000);
        return () => clearTimeout(t);
      }
    };
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online',  goOnline);
    window.addEventListener('offline', goOffline);
    window.__myloForceSave = () => { if (user) saveToCloud(st).catch(() => {}); };
    navigator.serviceWorker?.addEventListener('message', e => {
      if (e.data?.type === 'SYNC_REQUESTED' && supabase && user) saveToCloud(st);
    });
    return () => {
      window.removeEventListener('online',  goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [user, st, saveToCloud]); // eslint-disable-line

  // ── Keyboard shortcuts (desktop) ─────────────────────────────────────────────
  useEffect(() => {
    const h = e => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      if (e.metaKey || e.ctrlKey) {
        const map = { '1':'dashboard', '2':'tasks', '3':'ask', '4':'health', '5':'finance', 'n':'tasks' };
        if (map[e.key]) { e.preventDefault(); setTab(map[e.key]); }
        if (e.key === 'k') { e.preventDefault(); setQcOpen(true); }
      }
      if (e.key === 'Escape') { setQcOpen(false); setVoiceOpen(false); setMemShowing(null); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []); // keydown_shortcuts

  // ── Sign out ──────────────────────────────────────────────────────────────────
  const signOut = useCallback(async () => {
    if (supabase) await supabase.auth.signOut();
    setSt(SEED);
    saveState(SEED);
  }, []);
  useEffect(() => {
    window.__myloSignOut = signOut;
    return () => { window.__myloSignOut = null; };
  }, [signOut]);

  // ── Push notifications ────────────────────────────────────────────────────────
  const subscribeToPush = useCallback(async () => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return false;
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') return false;
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
        if (!vapidKey) {
          setSt(p => ({ ...p, prefs: { ...p.prefs, pushEnabled: true } }));
          return true;
        }
        const b64 = vapidKey.replace(/-/g, '+').replace(/_/g, '/');
        const raw = atob(b64.padEnd(b64.length + (4 - b64.length % 4) % 4, '='));
        const keyBytes = Uint8Array.from(raw, c => c.charCodeAt(0));
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes });
      }
      const subJson = JSON.stringify(sub);
      setSt(p => ({ ...p, prefs: { ...p.prefs, pushEnabled: true, pushSubscription: subJson } }));
      return true;
    } catch (e) {
      console.warn('Push subscribe failed:', e);
      return false;
    }
  }, []);

  // ── Daily briefs (morning / afternoon / evening) ──────────────────────────────
  const scheduleDailyBriefs = useCallback((_, state) => {
    if (!state.prefs?.pushEnabled) return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    const name = state.profile?.name?.split(' ')[0] || '';
    const now  = new Date();
    const today = now.toISOString().slice(0, 10);

    const schedule = (hourStr, key, buildNotif) => {
      const t = new Date(`${today}T${hourStr}:00`);
      const ms = t - now;
      if (ms <= 0 || ms >= 86400000) return;
      try { if (localStorage.getItem(key) === today) return; } catch {}
      setTimeout(() => {
        const notif = buildNotif();
        if (notif) {
          new Notification(notif.title, { body: notif.body, icon: '/icon-192.png', tag: key });
          try { localStorage.setItem(key, today); } catch {}
        }
      }, ms);
    };

    schedule('07:00', 'mylo_last_morning_brief', () => {
      const tasks   = state.tasks.filter(t => !t.done && t.bucket === 'today').length;
      const payday  = (state.income || []).find(i => i.nextDate && df(i.nextDate) <= 2 && df(i.nextDate) >= 0);
      const vinyl   = state.vinyl.find(v => !v.delivered && v.status === 'shipped' && df(v.eta) <= 3 && df(v.eta) >= 0);
      const parts   = [];
      if (tasks > 0)  parts.push(`${tasks} task${tasks > 1 ? 's' : ''} today`);
      if (payday)     parts.push('payday soon');
      if (vinyl)      parts.push(`${vinyl.artist} arriving`);
      return { title: `Good morning${name ? `, ${name}` : ''} ☀️`, body: parts.join(' · ') || 'Tap to see your day.' };
    });

    schedule('14:00', 'mylo_last_afternoon_nudge', () => {
      try {
        const alerts = buildProactiveAlerts(state);
        if (!alerts.length) return null;
        const top = alerts[0];
        return { title: `mylo · ${top.domain}`, body: top.body };
      } catch { return null; }
    });

    schedule('21:00', 'mylo_last_evening_brief', () => {
      const done    = state.tasks.filter(t => t.done && t.created === today).length;
      const pending = state.tasks.filter(t => !t.done && (t.bucket === 'today' || t.dueDate === today)).length;
      const workout = state.workouts.find(w => w.date === today);
      const parts   = [`${done} of ${done + pending} tasks`, workout ? workout.type.toLowerCase() + ' ✓' : 'no workout'];
      const tmrw = new Date(now); tmrw.setDate(tmrw.getDate() + 1);
        const tmrwStr = tmrw.toISOString().slice(0, 10);
        const tmrwTasks = state.tasks.filter(t => !t.done && t.dueDate === tmrwStr).length;
        const tmrwShift = (state.events || []).find(e => e.date === tmrwStr && /shift|work/i.test(e.type || e.title || ''));
        const tmrwParts = [];
        if (tmrwTasks > 0) tmrwParts.push(`${tmrwTasks} task${tmrwTasks > 1 ? 's' : ''}`);
        if (tmrwShift) tmrwParts.push(tmrwShift.title.slice(0, 20));
        const body = parts.join(', ') + (tmrwParts.length > 0 ? '. Tomorrow: ' + tmrwParts.join(', ') : '.');
        return { title: `Evening check-in${name ? ', ' + name : ''} 🌙`, body };
    });
  }, []);

  useEffect(() => {
    if (st.prefs?.pushEnabled && st.profile?.onboarded) scheduleDailyBriefs(null, st);
  }, [st.prefs?.pushEnabled]); 

  // ── Weekly pattern memory detection ─────────────────────────────────────────
  // Runs once per week, stores cross-domain correlations to st.memories[]
  useEffect(() => {
    if (!st.profile?.onboarded) return;
    const key = 'mylo_last_pattern_run';
    try {
      const last = localStorage.getItem(key);
      const now  = new Date().toISOString().slice(0, 10);
      if (last === now) return; // already ran today
      const daysSinceLast = last ? Math.floor((new Date(now) - new Date(last)) / 86400000) : 99;
      if (daysSinceLast < 7) return; // only run weekly
    } catch {}
    try {
      const newMemories = buildPatternMemories(st);
      if (newMemories.length === 0) return;
      setSt(p => {
        const existing = new Set((p.memories || []).map(m => m.key));
        const toAdd = newMemories.filter(m => !existing.has(m.key));
        const toUpdate = newMemories.filter(m => existing.has(m.key));
        const updated = (p.memories || []).map(m => {
          const upd = toUpdate.find(u => u.key === m.key);
          return upd ? { ...m, ...upd } : m;
        });
        return { ...p, memories: [...updated, ...toAdd] };
      });
      try { localStorage.setItem('mylo_last_pattern_run', new Date().toISOString().slice(0, 10)); } catch {}
    } catch (e) {
      console.warn('Pattern memory detection failed:', e);
    }
  }, [st.profile?.onboarded]); // eslint-disable-line
// eslint-disable-line

  // ── Onboarding complete ───────────────────────────────────────────────────────
  const handleOnboardingComplete = useCallback((data) => {
    const finalProfile = data.discogsSetup
      ? { ...data.profile, ...data.discogsSetup }
      : data.profile;
    setSt(p => ({
      ...p,
      profile:  finalProfile,
      finance:  data.finance  || [],
      transactions: data.transactions || [],
      goals:    data.goals    || [],
      tasks:    data.tasks    || [],
      memories: data.memories || [],
    }));
  }, []);

  // ── Computed values ───────────────────────────────────────────────────────────
  const sigs           = useMemo(() => { try { return buildSignals(st); }          catch { return []; } }, [st]);
  const insights       = useMemo(() => { try { return buildInsights(st, sigs); }   catch { return []; } }, [st, sigs]); // eslint-disable-line
  const brief          = useMemo(() => { try { return buildDailyBrief(st, sigs); } catch { return {}; } }, [st, sigs]);
  const sysPrompt      = useMemo(() => { try { return buildSystemPrompt(st, sigs); } catch { return 'You are Mylo, an AI assistant.'; } }, [st, sigs]);
  const patternInsights = useMemo(() => { try { return buildPatternInsights(st); } catch { return []; } }, [st]);
  const warnN          = sigs.filter(s => s.lvl === 'warn').length;

  // ── Actions ───────────────────────────────────────────────────────────────────
  const notify = useCallback((msg, dur = 2600) => {
    if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate([6, 0, 6]);
    setToast(msg);
    setTimeout(() => setToast(null), dur);
  }, []);

  const toggleTask = useCallback(id => setSt(p => {
    const task = p.tasks.find(t => t.id === id);
    if (!task) return p;
    const nowDone    = !task.done;
    const updatedTasks = p.tasks.map(t => t.id === id ? { ...t, done: nowDone } : t);
    if (nowDone && task.recurring && task.recurring !== 'none') {
      const base = new Date((task.dueDate || TODAY) + 'T12:00:00');
      if (task.recurring === 'daily')   base.setDate(base.getDate() + 1);
      if (task.recurring === 'weekly')  base.setDate(base.getDate() + 7);
      if (task.recurring === 'monthly') base.setMonth(base.getMonth() + 1);
      const nextDate = base.toISOString().slice(0, 10);
      const diff     = Math.round((new Date(nextDate) - new Date(TODAY)) / 86400000);
      const bucket   = diff <= 0 ? 'today' : diff <= 7 ? 'thisWeek' : 'later';
      const newTask  = { ...task, id: `t${Date.now()}`, done: false, dueDate: nextDate, bucket, created: TODAY };
      return { ...p, tasks: [...updatedTasks, newTask] };
    }
    return { ...p, tasks: updatedTasks };
  }), []);

  const markDelivered = useCallback(id => {
    setSt(p => ({ ...p, vinyl: p.vinyl.map(v => v.id === id ? { ...v, delivered: true, status: 'delivered' } : v) }));
    notify('Marked delivered');
  }, [notify]);

  const submitQC = useCallback(() => {
    if (!qcVal.trim()) return;
    const low  = qcVal.toLowerCase();
    const dest = /vinyl|record/i.test(low)     ? 'vinyl'
               : /sub|bill|finance/i.test(low) ? 'finance'
               : /task|remind/i.test(low)      ? 'tasks'
               : /workout|run|gym/i.test(low)  ? 'health'
               : /goal/i.test(low)             ? 'goals'
               : /event|calendar/i.test(low)   ? 'calendar'
               : 'ask';
    setQcRes(`→ Routing to ${dest}`);
    setTimeout(() => { setQcOpen(false); setQcVal(''); setQcRes(null); setTab(dest); notify('Captured'); }, 700);
  }, [qcVal, notify]);

  const handleTabChange = useCallback(id => {
    if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(8);
    setTab(id);
    setMobNavOpen(false);
    setTimeout(() => { document.querySelector('.main')?.scrollTo(0, 0); }, 0);
  }, []);

  // ── Autonomous memory detection ───────────────────────────────────────────────
  useEffect(() => {
    if (!st.profile?.onboarded) return;
    const novel = detectMemories(st).filter(c => !seenMemKeysRef.current.has(c.key));
    if (novel.length > 0) {
      novel.forEach(c => seenMemKeysRef.current.add(c.key));
      setMemQueue(q => [...q, ...novel]);
    }
  }, [st]);

  useEffect(() => {
    if (memShowing || memQueue.length === 0) return;
    const [next, ...rest] = memQueue;
    setMemQueue(rest);
    setMemShowing(next);
  }, [memQueue, memShowing]);

  const confirmMemory = useCallback(() => {
    if (!memShowing) return;
    setSt(p => ({ ...p, memories: [{ id: `m${Date.now()}`, cat: memShowing.cat, text: memShowing.text, key: memShowing.key }, ...p.memories] }));
    setMemShowing(null);
    notify('Memory saved');
  }, [memShowing, notify]);

  const dismissMemory = useCallback(() => setMemShowing(null), []);

  // ── Page map ──────────────────────────────────────────────────────────────────
  const pages = {
    dashboard: <DashboardPage brief={brief} sigs={sigs} patterns={patternInsights} st={st} setTab={setTab} toggleTask={toggleTask} setSt={setSt} notify={notify} />,
    ask:       <ChatPage sysPrompt={sysPrompt} anthropicKey={st.anthropicKey || ''} setSt={setSt} notify={notify} setTab={setTab} openVoice={() => setVoiceOpen(true)} pendingEmailActions={st.pendingEmailActions || []} vinyl={st.vinyl} events={st.events} brief={brief} sigs={sigs} st={st} />,
    calendar:  <CalendarPage events={st.events} tasks={st.tasks} view={calView} setView={setCalView} setSt={setSt} notify={notify} autoSync={!!user} />,
    tasks:     <TasksPage tasks={st.tasks} lists={st.lists} routines={st.routines || []} quickLists={st.quickLists || []} toggleTask={toggleTask} setSt={setSt} notify={notify} />,
    goals:     <GoalsPage goals={st.goals} setSt={setSt} notify={notify} />,
    vinyl:     <VinylPage vinyl={st.vinyl} markDelivered={markDelivered} showAdd={showAddV} setShowAdd={setShowAddV} setSt={setSt} notify={notify} savedDiscogsToken={st.discogsToken || ''} savedDiscogsUser={st.profile?.discogsUser || ''} />,
    finance:   <FinancePage finance={st.finance} income={st.income || []} budgets={st.budgets} categories={st.financeCategories && st.financeCategories.length > 0 ? st.financeCategories : ['Housing','Food','Transport','Health','Entertainment','Subscriptions','Other']} showAdd={showAddF} setShowAdd={setShowAddF} setSt={setSt} notify={notify} st={st} />,
    health:    <HealthPage workouts={st.workouts} goals={st.goals} events={st.events} coachHistory={st.coachHistory || []} customWorkoutTypes={st.customWorkoutTypes || []} setSt={setSt} notify={notify} />,
    settings:  <SettingsPage st={st} prefs={st.prefs} anthropicKey={st.anthropicKey || ''} setSt={setSt} memories={st.memories} subscribeToPush={subscribeToPush} discogsToken={st.discogsToken || ''} />,
    reminders: <RemindersPage reminders={st.reminders} prefs={st.prefs} st={st} setSt={setSt} notify={notify} />,
    inbox:     <InboxPage inbox={st.inbox} pendingEmailActions={st.pendingEmailActions} emailLog={st.emailLog} vinyl={st.vinyl} events={st.events} setSt={setSt} notify={notify} setTab={setTab} autoSync={!!user} />,
    timeline:  <TimelinePage tl={st.timeline} setSt={setSt} notify={notify} />,
    contacts:  <ContactsPage contacts={st.contacts} setSt={setSt} notify={notify} />,
    weekly:    <WeeklyPage sigs={sigs} st={st} setTab={setTab} setSt={setSt} notify={notify} />,
  };

  const isChat = tab === 'ask';

  // ── Auth gates ────────────────────────────────────────────────────────────────

  if (supabase && !authReady) {
    return (
      <>
        <style>{CSS}</style>
        <HideSplash />
        <div style={{ position:'fixed', inset:0, background:'var(--bg)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'var(--sp3)' }}>
          <img src="/icon-192.png" alt="Mylo" style={{ width:48, height:48, borderRadius:'var(--r2)', opacity:.6 }} />
          <div style={{ fontSize:'var(--fs-xs)', color:'var(--t4)', fontFamily:'var(--mono)', letterSpacing:'.2em', textTransform:'uppercase' }}>Loading…</div>
        </div>
      </>
    );
  }

  if (supabase && !user) {
    return (
      <>
        <style>{CSS}</style>
        <HideSplash />
        <LoginScreen />
      </>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────────

  return (
    <ErrorBoundary>
      <style>{CSS}</style>
      <HideSplash />

      {!st.profile?.onboarded && (
        <OnboardingFlow onComplete={handleOnboardingComplete} />
      )}

      <div className="app" style={{ visibility: st.profile?.onboarded ? 'visible' : 'hidden' }}>

        {/* Morning brief */}
        {!briefDismissed && st.profile?.onboarded && (
          <MorningBrief brief={brief} sigs={sigs} st={st} setTab={handleTabChange}
            onDismiss={() => {
              setBriefDismissed(true);
              try { localStorage.setItem('mylo_brief_dismissed', new Date().toISOString().slice(0, 10)); } catch {}
            }}
          />
        )}

        {/* Offline bar */}
        {!isOnline && (
          <div className="offline-bar">
            <div className="offline-pill">
              ◌ Offline — changes saved locally{queuedOps > 0 ? ` (${queuedOps} pending sync)` : ''}
            </div>
          </div>
        )}

        {/* Mobile nav overlay */}
        {mobNavOpen && <div className="mob-overlay" onClick={() => setMobNavOpen(false)} />}

        {/* Sidebar */}
        <aside className={`sb${mobNavOpen ? ' open' : ''}`}>
          <div className="sb-logo">
            <div className="sb-wordmark">My<span className="sb-wordmark-dot">.</span>lo</div>
          </div>
          <div className="sb-nav">
            <div className="sb-group-label">workspace</div>
            {NAV_CORE.map(n => {
              const badge = n.id === 'dashboard' && warnN > 0 ? warnN
                          : n.id === 'inbox'     && (st.pendingEmailActions || []).length > 0 ? (st.pendingEmailActions || []).length
                          : null;
              return (
                <div key={n.id} className={`ni${tab === n.id ? ' on' : ''}`}
                  onClick={() => handleTabChange(n.id)}>
                  <span className="ni-ic">{n.icon}</span>
                  <span className="ni-lbl">{n.label}</span>
                  {badge ? <span className="ni-badge">{badge}</span> : null}
                </div>
              );
            })}
            <div className="sb-group-label" style={{marginTop:8}}>review</div>
            {NAV_ADV.map(n => {
              const badge = n.id === 'inbox'    && (st.pendingEmailActions || []).length > 0 ? (st.pendingEmailActions || []).length
                          : n.id === 'settings' && memQueue.length > 0 ? memQueue.length
                          : null;
              return (
                <div key={n.id} className={`ni ni-secondary${tab === n.id ? ' on' : ''}`}
                  onClick={() => handleTabChange(n.id)}>
                  <span className="ni-ic">{n.icon}</span>
                  <span className="ni-lbl">{n.label}</span>
                  {badge ? <span className="ni-badge">{badge}</span> : null}
                </div>
              );
            })}
          </div>
          <div style={{ margin:'6px 8px 0', display:'flex', alignItems:'center', gap:7, padding:'7px 10px', borderRadius:6, background:'var(--aw)', border:'0.5px solid var(--ab)', fontSize:12, color:'var(--accent)', cursor:'pointer' }} onClick={function(){handleTabChange('ask');}}>
            <span style={{ fontSize:13 }}>✦</span> Ask Mylo
          </div>
          <div className="sb-foot">
            <div style={{ display:'flex', alignItems:'center', gap:6, padding:'4px 8px', fontFamily:'var(--mono)', fontSize:10, color:'var(--t4)' }}>
              <div style={{ width:5, height:5, borderRadius:'50%', background: !isOnline ? 'var(--t4)' : syncing ? 'var(--gold)' : 'var(--grn)', flexShrink:0 }} />
              <span>{!isOnline ? 'offline' : syncing ? 'syncing...' : 'synced'} · {new Date().toLocaleDateString('en-US',{month:'short',day:'numeric'}).toLowerCase()}</span>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className={`main${isChat ? ' is-chat' : ''}${tab === 'calendar' ? ' is-calendar' : ''}`}>

          {/* Mobile header */}
          <div className="mob-header" style={{position:'relative'}}>
            <button className="mob-menu-btn" onClick={() => setMobNavOpen(p => !p)} style={{fontSize:18,width:36,height:36,display:"flex",alignItems:"center",justifyContent:"center"}}>☰</button>
            <div className="mob-logo" style={{position:'absolute',left:'50%',transform:'translateX(-50%)'}}>
              My<span className="mob-logo-dot">.</span>lo
              {supabase && user && !isOnline && (
                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--t3)', fontFamily: 'var(--mono)', marginLeft: 'var(--sp2)', verticalAlign: 'middle', opacity: .7 }}>
                  ○
                </span>
              )}
            </div>
            <div className="row-g2">
              <button className="mob-qc-btn" onClick={() => setVoiceOpen(true)} title="Voice">◎</button>
              <button className="mob-qc-btn" onClick={() => setQcOpen(true)}>+</button>
            </div>
          </div>

          {/* Page content */}
          {isChat
            ? <div style={{ flex:1, overflow:'hidden', display:'flex', flexDirection:'column', minHeight:0 }}>{pages.ask}</div>
            : tab === 'calendar'
              ? <div style={{ flex:1, overflow:'hidden', display:'flex', flexDirection:'column', minHeight:0 }}>{pages.calendar}</div>
              : <div className="main-inner">
                  {false && st.profile?.onboarded && !st.prefs?.shiftMode && (
                    <CommandBar setSt={setSt} notify={notify} setTab={handleTabChange} sysPrompt={sysPrompt} anthropicKey={st.anthropicKey || ''} />
                  )}
                  <div className="page">
                    {(() => {
                      try { return <ErrorBoundary>{pages[tab] || pages.dashboard}</ErrorBoundary>; }
                      catch (e) {
                        console.error('[Mylo] Page crash:', e.message);
                        return (
                          <div className="page-error">
                            <div className="page-error-icon">⚠</div>
                            <div className="page-error-title">Something went wrong</div>
                            <div className="page-error-msg">{e.message}</div>
                            <button className="page-error-btn btn btn-sm" onClick={() => window.location.reload()}>Reload</button>
                            <button className="page-error-btn btn btn-sm mt3" style={{ marginLeft: 'var(--sp2)' }} onClick={() => setTab('ask')}>Go to Mylo</button>
                          </div>
                        );
                      }
                    })()}
                  </div>
                </div>
          }
        </main>

        {/* Bottom nav — mobile */}
        <nav className="bot-nav">
          <div className="bot-nav-inner">
            {BOT_NAV.map(n => (
              <div key={n.id}
                className={`bot-ni${tab === n.id ? ' on' : ''}${n.fab ? ' is-fab' : ''}${n.primary ? ' is-primary' : ''}`}
                onClick={() => handleTabChange(n.id)}
                role="button"
                aria-label={n.label}
              >
                {n.id === 'dashboard' && warnN > 0 && <span className="bot-ni-dot" />}
                {n.id === 'ask' && (st.pendingEmailActions || []).length > 0 && <span className="bot-ni-dot" />}
                <span className="bot-ni-ic">
                  {n.icon === 'heart' ? (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 17C10 17 3 12.5 3 7.5a4 4 0 0 1 7-2.65A4 4 0 0 1 17 7.5C17 12.5 10 17 10 17Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg>
                  ) : n.icon === 'cal' ? (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="3" y="4" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.3"/><path d="M3 8h14" stroke="currentColor" strokeWidth="1.2"/><path d="M7 2v3M13 2v3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
                  ) : n.icon === 'M.' ? (
                    <span style={{fontSize:16,fontWeight:700,letterSpacing:'-0.03em',lineHeight:1}}>M<span style={{color:'var(--al)'}}>.</span></span>
                  ) : n.icon}
                </span>
                <span className="bot-ni-lbl">{n.label}</span>
              </div>
            ))}
          </div>
        </nav>


        {/* Quick capture modal */}
        {qcOpen && (
          <div className="qc-bg" onClick={() => setQcOpen(false)}>
            <div className="qc-box" onClick={e => e.stopPropagation()}>
              <input autoFocus className="qc-input" placeholder="Capture anything…"
                value={qcVal} onChange={e => setQcVal(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') submitQC(); }} />
              <div className="qc-chips">
                {['/add task', '/add vinyl', '/add subscription', '/add goal', '/log workout', '/log payday'].map(h => (
                  <button key={h} className="qc-chip" onClick={() => setQcVal(h + ' ')}>{h}</button>
                ))}
              </div>
              {qcRes && <div className="qc-result">{qcRes}</div>}
            </div>
          </div>
        )}

        {voiceOpen && (
          <VoiceModal close={() => setVoiceOpen(false)} sysPrompt={sysPrompt} notify={notify} setSt={setSt} setTab={setTab} />
        )}

        {/* Memory notification */}
        {memShowing && (
          <div className="mem-notify">
            <div className="mem-notify-icon">◈</div>
            <div className="mem-notify-body">
              <div className="mem-notify-label">Mylo learned something</div>
              <div className="mem-notify-msg">{memShowing.label}</div>
              <div className="mem-notify-actions">
                <button className="mem-notify-confirm" onClick={confirmMemory}>Save to memory</button>
                <button className="mem-notify-dismiss" onClick={dismissMemory}>Dismiss</button>
              </div>
            </div>
          </div>
        )}

        {toast && <div className="toast">{toast}</div>}
      </div>

      {/* Shift mode overlay */}
      {st.profile?.onboarded && st.prefs?.shiftMode && (
        <div style={{ position:'fixed', inset:0, zIndex:500, background:'var(--bg)', overflowY:'auto' }}>
          <ShiftMode st={st} setTab={handleTabChange} setSt={setSt} notify={notify} onVoice={() => setVoiceOpen(true)} />
        </div>
      )}

      {/* Command bar — renders inside main-inner on non-chat/calendar pages.
          For chat and calendar (which bypass main-inner with their own layouts),
          render at root so it stays available via mobile's fixed-position CSS. */}
      {false && st.profile?.onboarded && !st.prefs?.shiftMode && (isChat || tab === 'calendar') && (
        <CommandBar setSt={setSt} notify={notify} setTab={handleTabChange} sysPrompt={sysPrompt} anthropicKey={st.anthropicKey || ''} />
      )}
    </ErrorBoundary>
  );
}
